import { Box, Typography, Button, useTheme } from '@mui/material';
import { useTranslate } from 'react-admin';
import InboxIcon from '@mui/icons-material/Inbox';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

// Import WhatsApp-style backgrounds
import waBackgroundLight from '../../assets/wa-background-light.png';
import waBackgroundDark from '../../assets/wa-background-dark.png';

interface EmptyConversationStateProps {
  /** Custom title (optional) */
  title?: string;
  /** Custom subtitle (optional) */
  subtitle?: string;
  /** Show action button to start conversation (optional) */
  showActionButton?: boolean;
  /** Callback when action button is clicked (optional) */
  onActionClick?: () => void;
  /** Custom icon to display (defaults to ChatBubbleOutlineIcon) */
  icon?: React.ReactNode;
}

/**
 * EmptyConversationState - WhatsApp-style empty state when no conversation/messages
 *
 * Features:
 * - WhatsApp background (light/dark mode aware)
 * - Customizable title, subtitle, and icon
 * - Optional action button
 * - Responsive design
 *
 * @example
 * ```tsx
 * // Basic usage (no ticket selected)
 * <EmptyConversationState
 *   title="Nenhum ticket selecionado"
 *   subtitle="Selecione um ticket para visualizar a conversa"
 * />
 *
 * // With action button (no messages)
 * <EmptyConversationState
 *   title="Nenhuma mensagem ainda"
 *   subtitle="Inicie a conversa!"
 *   showActionButton
 *   onActionClick={() => console.log('Start conversation')}
 * />
 * ```
 */
export function EmptyConversationState({
  title,
  subtitle,
  showActionButton = false,
  onActionClick,
  icon,
}: EmptyConversationStateProps) {
  const translate = useTranslate();
  const theme = useTheme();

  // Select appropriate background based on theme
  const waBackground = theme.palette.mode === 'dark' ? waBackgroundDark : waBackgroundLight;

  // Default icon
  const displayIcon = icon || <ChatBubbleOutlineIcon sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.3 }} />;

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: 200,
        bgcolor: waBackground ? 'transparent' : 'background.default',
        backgroundImage: waBackground ? `url(${waBackground})` : undefined,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        px: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          maxWidth: 400,
          textAlign: 'center',
          bgcolor: theme.palette.background.overlay,
          backdropFilter: 'blur(8px)',
          p: 4,
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        {displayIcon}

        {title && (
          <Typography variant="h6" color="textPrimary" fontWeight={500}>
            {title}
          </Typography>
        )}

        {subtitle && (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        )}

        {showActionButton && onActionClick && (
          <Button
            variant="contained"
            color="primary"
            onClick={onActionClick}
            sx={{ mt: 1, textTransform: 'none' }}
          >
            {translate('resources.chat.startConversation')}
          </Button>
        )}
      </Box>
    </Box>
  );
}
